import { useEffect, useMemo, useRef, useState } from 'react'
import { ReminderCard } from '../components/ReminderCard'
import { UNCATEGORIZED } from '../components/CategoryIcon'
import { VerifyBanner } from '../components/VerifyBanner'
import { FREE_LIMIT, activeCount, useStore } from '../lib/store'
import { api, type Category, type CategoryDef, type Reminder } from '../lib/api'
import { APP_VERSION, buildLabel } from '../lib/version'
import './Home.css'

type Filter = { kind: 'all' } | { kind: 'cat'; slug: Category } | { kind: 'none' }

interface Chip {
  slug: Category
  label: string
}

/** The chip row is the user's own categories, plus any category a shared
 * reminder resolved through its embedded copy — otherwise a household member's
 * reminder would be reachable from "All" and nowhere else. */
function chipsFor(open: Reminder[], categories: CategoryDef[]): Chip[] {
  const chips: Chip[] = categories.map((c) => ({ slug: c.slug, label: c.label }))
  const seen = new Set(chips.map((c) => c.slug))
  for (const r of open) {
    if (seen.has(r.category) || !r.category_detail) continue
    seen.add(r.category)
    chips.push({ slug: r.category, label: r.category_detail.label })
  }
  return chips
}

export function Home({
  onAdd,
  onUpgrade,
  onShowDone,
  onShowVersion,
}: {
  onAdd: () => void
  onUpgrade: () => void
  onShowDone: () => void
  onShowVersion: () => void
}) {
  const { reminders, categories, nearbyIds, user, refresh, locationError, watching } =
    useStore()
  // A slug is an open string, so the two synthetic filters can't be encoded as
  // sentinel slugs without risking a collision with a real category.
  const [filter, setFilter] = useState<Filter>({ kind: 'all' })
  const [busy, setBusy] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  // A popover that only closes on its own button is a trap on touch.
  useEffect(() => {
    if (!menuOpen) return
    const onPointer = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false)
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  const open = useMemo(() => reminders.filter((r) => !r.completed_at), [reminders])

  const counts = useMemo(() => {
    const tally = new Map<Category, number>()
    for (const r of open) tally.set(r.category, (tally.get(r.category) ?? 0) + 1)
    return tally
  }, [open])

  const chips = useMemo(() => chipsFor(open, categories), [open, categories])

  const orphans = useMemo(() => {
    const known = new Set(chips.map((c) => c.slug))
    return open.filter((r) => !known.has(r.category))
  }, [open, chips])

  const visible = useMemo(() => {
    if (filter.kind === 'all') return open
    if (filter.kind === 'none') return orphans
    return open.filter((r) => r.category === filter.slug)
  }, [open, orphans, filter])

  // Deleting the filtered category (or clearing the last stray) would otherwise
  // strand the user on a chip that no longer exists, staring at an empty list.
  useEffect(() => {
    if (filter.kind === 'cat' && !chips.some((c) => c.slug === filter.slug)) {
      setFilter({ kind: 'all' })
    }
    if (filter.kind === 'none' && orphans.length === 0) setFilter({ kind: 'all' })
  }, [filter, chips, orphans])

  const active = activeCount(reminders)
  const atLimit = user?.tier === 'free' && active >= FREE_LIMIT

  async function toggle(r: Reminder) {
    setBusy(r.id)
    try {
      await api.patchReminder(r.id, { active: !r.active })
      await refresh()
    } finally {
      setBusy(null)
    }
  }

  async function complete(r: Reminder) {
    setBusy(r.id)
    try {
      await api.completeReminder(r.id)
      if (navigator.vibrate) navigator.vibrate(18)
      await refresh()
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <header className="nav">
        <div className="menu menu--left" ref={menuRef}>
          <button
            className="avatar"
            aria-label="Menu"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="5" cy="12" r="1.9" />
              <circle cx="12" cy="12" r="1.9" />
              <circle cx="19" cy="12" r="1.9" />
            </svg>
          </button>

          {menuOpen && (
            <div className="menu__pop" role="menu">
              <button
                role="menuitem"
                className="menu__item"
                onClick={() => {
                  setMenuOpen(false)
                  onShowDone()
                }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 12.5 9.5 18 20 6.5" />
                </svg>
                Done
              </button>

              <hr className="menu__rule" />

              <button
                role="menuitem"
                className="menu__item menu__item--stack"
                onClick={() => {
                  setMenuOpen(false)
                  onShowVersion()
                }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 11v5.5M12 7.8v.1" />
                </svg>
                <span>
                  Version {APP_VERSION}
                  <small>{buildLabel()}</small>
                </span>
              </button>
            </div>
          )}
        </div>

        <h1>Nudge</h1>

        <span className={`live${watching ? ' live--on' : ''}`} title={
          watching ? 'Tracking your location' : 'Location off'
        }>
          <i />
        </span>
      </header>

      <div className="scroll">
        <VerifyBanner />

        <div className="chips" role="tablist" aria-label="Filter by category">
          <button
            role="tab"
            className="chip"
            aria-pressed={filter.kind === 'all'}
            onClick={() => setFilter({ kind: 'all' })}
          >
            All <span className="chip__n">({open.length})</span>
          </button>
          {chips.map((c) => (
            <button
              key={c.slug}
              role="tab"
              className="chip"
              aria-pressed={filter.kind === 'cat' && filter.slug === c.slug}
              onClick={() => setFilter({ kind: 'cat', slug: c.slug })}
            >
              {c.label} <span className="chip__n">({counts.get(c.slug) ?? 0})</span>
            </button>
          ))}
          {orphans.length > 0 && (
            <button
              role="tab"
              className="chip"
              aria-pressed={filter.kind === 'none'}
              onClick={() => setFilter({ kind: 'none' })}
            >
              {UNCATEGORIZED.label} <span className="chip__n">({orphans.length})</span>
            </button>
          )}
        </div>

        {locationError && (
          <p className="banner banner--error" role="alert">
            {locationError}
          </p>
        )}

        {!watching && !locationError && (
          <p className="banner">
            Location is off, so nothing can trigger. Turn it on in Profile.
          </p>
        )}

        <div className="stack">
          {visible.map((r, i) => (
            <div key={r.id} className={busy === r.id ? 'is-busy' : undefined}>
              <ReminderCard
                reminder={r}
                nearby={nearbyIds.has(r.id)}
                index={i}
                onToggle={toggle}
                onComplete={complete}
              />
            </div>
          ))}

          {visible.length === 0 && (
            <div className="empty">
              <h2>{filter.kind === 'all' ? 'Nothing to pick up' : 'Nothing in this list'}</h2>
              <p>
                {filter.kind === 'all'
                  ? 'Add a reminder and Nudge will find you when you’re near a place that can tick it off.'
                  : 'Try another category, or add something here.'}
              </p>
            </div>
          )}

          {atLimit && (
            <div className="limit glass rise" role="status">
              <div>
                <strong>
                  You&rsquo;ve hit your limit ({active}/{FREE_LIMIT})
                </strong>
                <p>Upgrade to Pro for unlimited nudges.</p>
              </div>
              <button className="btn btn--amber btn--sm" onClick={onUpgrade}>
                Upgrade
              </button>
            </div>
          )}
        </div>
      </div>

      <button className="fab" onClick={onAdd} aria-label="New reminder">
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>
    </>
  )
}
